import type { IntlShape } from 'react-intl';
import type { MonthlyAddOnKind } from 'fym-shared/src/music24/monthlyAddOns';
import type { AddOnRowKind, AddOnRowModel, AddOnTierChip } from './addOnRowModel';
import { monthlyAddOnMessages as messages } from './monthlyAddOnMessages';

/** US cents as a dollar price with cents ("$17.00"). The add-ons are billed in US dollars or euros at the same number. */
export function formatAddOnCents(intl: IntlShape, cents: number): string {
  return intl.formatNumber(cents / 100, { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
}

/** The label of one tier chip ("101–500 · $0.07"), or the one chip of the extra artists ("$15.00 an artist"). */
export function tierChipLabel(intl: IntlShape, chip: AddOnTierChip, kind: AddOnRowKind): string {
  const price = formatAddOnCents(intl, chip.unitCents);
  if (kind === 'extraArtists') {
    return intl.formatMessage(messages.artistsChip, { price });
  }
  return chip.to === null
    ? intl.formatMessage(messages.tierOpen, { from: intl.formatNumber(chip.from), price })
    : intl.formatMessage(messages.tierRange, {
        from: intl.formatNumber(chip.from),
        to: intl.formatNumber(chip.to),
        price,
      });
}

const HINTS: Record<
  MonthlyAddOnKind,
  { first: typeof messages.postsFirst; next: typeof messages.postsNext; more: typeof messages.postsMore }
> = {
  posts: { first: messages.postsFirst, next: messages.postsNext, more: messages.postsMore },
  videoCredits: { first: messages.creditsFirst, next: messages.creditsNext, more: messages.creditsMore },
};

/** The tier hint under a slider ("Next 300 posts: $0.07 each"), or null at the end of the slider. */
export function tierHint(intl: IntlShape, model: AddOnRowModel): string | null {
  if (model.next === null || model.kind === 'extraArtists') {
    return null;
  }
  const price = formatAddOnCents(intl, model.next.unitCents);
  const hints = HINTS[model.kind];
  if (model.next.unitsLeftInTier === null) {
    return intl.formatMessage(hints.more, { price });
  }
  const count = intl.formatNumber(model.next.unitsLeftInTier);
  return intl.formatMessage(model.quantity === 0 ? hints.first : hints.next, { count, price });
}
